import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Dealer } from 'src/app/shared/dealer/models/dealer';
import { DealerState } from 'src/app/shared/state/DealerState';

import { StateManagementService } from '../../../shared/services/state-management.service';
import { SessionRehydrationService } from '../../../shared/services/session-rehydration.service';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  public router = inject(Router);
  public authenticationService = inject(AuthenticationService);
  public stateManagementService = inject(StateManagementService);
  public store = inject(Store);
  public sessionRehydrationService = inject(SessionRehydrationService);

  constructor() {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    // Token missing or genuinely expired -> real logout, safe to clear storage everywhere.
    if (!this.authenticationService.isAuthenticated() || this.authenticationService.isTokenExpired()) {
      return this.redirectToLogin(state, true);
    }

    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (dealer !== null) {
      return true;
    }

    return this.sessionRehydrationService
      .restoreDealer()
      .pipe(map((restoredDealer) => (restoredDealer !== null ? true : this.redirectToLogin(state, true))));
  }

  private redirectToLogin(state: RouterStateSnapshot, clearStorage: boolean): false {
    if (clearStorage) {
      this.clearStorageAndResetStates();
    }
    if (state.url.startsWith('/home/dealer')) {
      this.router.navigate(['/authlogin'], { queryParams: { returnUrl: state.url } });
    } else {
      this.router.navigate(['/authlogin']);
    }
    return false;
  }

  checkUserLogin(route: ActivatedRouteSnapshot): boolean {
    if (this.authenticationService.isAuthenticated()) {
      const roles: string = this.authenticationService.getRoles();
      return route.data.role && roles.includes(route.data.role);
    }
  }

  clearStorageAndResetStates() {
    // Keep user-specific column preferences across logout and re-login.
    const preservedPrefix = 'columnPrefs:';
    const preserved: { [key: string]: string | null } = {};
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(preservedPrefix)) {
        preserved[key] = window.localStorage.getItem(key);
      }
    }

    window.localStorage.clear();
    Object.keys(preserved).forEach((key) => {
      const value = preserved[key];
      if (value !== null) {
        window.localStorage.setItem(key, value);
      }
    });
    this.stateManagementService.resetAllStates();
  }
}
